import { lazy } from "react";
import type { RouteObject } from "react-router-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { accountUpdateLoader } from "../../app/router/guards/loaders/account-update.loader";
import DefaultLayout from "../../components/layouts/default-layout/DefaultLayout";
import RouteWrapper from "../../components/layouts/route-wrapper/RouteWrapper";
import { SignaturePageLayout } from "../../components/layouts/SignaturePageLayoutV2";
import { SETTINGS_PAGE_TITLE } from "../../constants/commonStrings";
import { LOGGED_HOME_PAGE_PATH, SETTINGS_PATH } from "../../constants/env";
import { RouteWrapperVariant } from "../../constants/layouts/route-wrapper-variant.enum";
import CloseChatbotPanelWrapper from "../../legacy/components/Chatbot/wrappers/close-chatbot-panel-wrapper/close-chatbot-panel-wrapper";
import { HeaderModalBar } from "../../legacy/components/HeaderModalBar/header-modal-bar.component";
import SEO from "../../legacy/components/Seo";
import { SignatureSettingsLayout } from "../../legacy/pages/SettingsPage/layouts/signature-settings.layout";
import {
  MANAGE_SUBSCRIPTION_PATH,
  MANAGE_SUBSCRIPTION_TEXT,
} from "../../legacy/pages/SettingsPage/settings-routes.const";
import { SignDocumentHeader } from "../../legacy/pages/SignatureDocumentPage/components/sign-document-header";
import { withSuspense } from "./utils/with-suspense";

const SignatureSettingsPage = lazy(() => import("../../legacy/pages/SettingsPage/SignatureSettingsPage"));
const NotificationsHandlerPage = lazy(
  () =>
    import(
      "../../legacy/pages/SettingsPage/pages/NotificationsHandlerPage/components/notifications-handler-page/notifications-handler-page"
    )
);
const DeleteProfilePage = lazy(
  () => import("../../legacy/pages/SettingsPage/pages/delete-profile-page/delete-profile-page")
);
const ManageSubscriptionPage = lazy(
  () => import("../../legacy/pages/SettingsPage/pages/manage-subscription-page/manage-subscription-page")
);
const SettingsPage = lazy(() => import("../../legacy/pages/SettingsPage/settings-page"));
const ContactDetails = lazy(() => import("../../legacy/pages/ContactDetailsPage"));
const SignDocumentPage = lazy(() => import("../../legacy/pages/SignatureDocumentPage/SignDocumentPage"));

function SignDocumentHeaderWrapper() {
  const navigate = useNavigate();
  const location = useLocation();
  return <SignDocumentHeader navigate={navigate} location={location} />;
}

export function createSettingsRoutes(): RouteObject[] {
  return [
    // Signature settings
    {
      path: "/settings/signature",
      loader: accountUpdateLoader,
      element: (
        <>
          <SEO title="Modifica firma digitale" />
          <SignatureSettingsLayout header={<HeaderModalBar title="Firma" />}>
            <CloseChatbotPanelWrapper>{withSuspense(<SignatureSettingsPage />)}</CloseChatbotPanelWrapper>
          </SignatureSettingsLayout>
        </>
      ),
    },

    // Sign document
    {
      path: "/sign-document/:documentId",
      loader: accountUpdateLoader,
      element: (
        <SignaturePageLayout header={<SignDocumentHeaderWrapper />}>
          {withSuspense(<SignDocumentPage />)}
        </SignaturePageLayout>
      ),
    },

    // Settings contact details
    {
      path: "/settings/contact-details",
      loader: accountUpdateLoader,
      element: (
        <DefaultLayout>
          <SEO title="Recapiti" />
          <RouteWrapper
            variant={RouteWrapperVariant.secondLevelWithTitleAndAiAssistant}
            useLegacyDesign
            defaultBackButtonUrl={SETTINGS_PATH}
            title="Recapiti"
          >
            {withSuspense(<ContactDetails />)}
          </RouteWrapper>
        </DefaultLayout>
      ),
    },

    // Settings notifications
    {
      path: "/settings/notifications",
      loader: accountUpdateLoader,
      element: (
        <DefaultLayout>
          <SEO title="Notifiche" />
          <RouteWrapper
            variant={RouteWrapperVariant.secondLevelWithTitleAndAiAssistant}
            defaultBackButtonUrl={LOGGED_HOME_PAGE_PATH}
            title="Gestione Notifiche"
          >
            {withSuspense(<NotificationsHandlerPage />)}
          </RouteWrapper>
        </DefaultLayout>
      ),
    },

    // Settings manage subscription
    {
      path: `${SETTINGS_PATH}/${MANAGE_SUBSCRIPTION_PATH}`,
      loader: accountUpdateLoader,
      element: (
        <DefaultLayout>
          <SEO title={MANAGE_SUBSCRIPTION_TEXT} />
          <RouteWrapper
            variant={RouteWrapperVariant.secondLevelWithTitleAndAiAssistantFullHeight}
            defaultBackButtonUrl={SETTINGS_PATH}
            title={MANAGE_SUBSCRIPTION_TEXT}
          >
            {withSuspense(<ManageSubscriptionPage />)}
          </RouteWrapper>
        </DefaultLayout>
      ),
    },

    // Settings delete account
    {
      path: "/settings/delete-account",
      loader: accountUpdateLoader,
      element: (
        <DefaultLayout>
          <SEO title="Cancella account" />
          <RouteWrapper
            variant={RouteWrapperVariant.secondLevelWithTitleAndAiAssistant}
            defaultBackButtonUrl={SETTINGS_PATH}
            title="Cancella account"
          >
            {withSuspense(<DeleteProfilePage />)}
          </RouteWrapper>
        </DefaultLayout>
      ),
    },

    // Settings
    {
      path: SETTINGS_PATH,
      loader: accountUpdateLoader,
      element: (
        <DefaultLayout>
          <SEO title={SETTINGS_PAGE_TITLE} />
          <RouteWrapper>{withSuspense(<SettingsPage />)}</RouteWrapper>
        </DefaultLayout>
      ),
    },
  ];
}
